import React from 'react';
import styled from 'styled-components';
import { DatePicker as AntDatePicker, TimePicker as AntTimePicker, TimePickerProps } from 'antd';
import { CalendarIcon } from 'assets/svg';

const DatePickerPanelContainer = styled.div<{ $fontFamily?: string; $color?: string }>`
  * {
    font-family: ${(props) => props.$fontFamily} !important;
  }

  .ant-picker-content th,
  .ant-picker-content td {
    &::before {
      display: none;
    }
    > div {
      &:hover {
        background-color: var(--grey-light);
        color: ${(props) => props.$color || 'var(--orange)'};
        border-radius: 6px;
      }
      padding-top: 2px;

      &.disabled {
        color: var(--grey);
        background-color: var(--grey-light);
      }
      &.overlapped {
        color: var(--green-dark);
        background-color: var(--grey-light);
      }
      &.selected {
        border-radius: 6px;
        color: white;
      }
    }
  }
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
    background-color: ${(props) => props.$color || 'var(--indian-red)'};
    &:before {
      border-color: ${(props) => props.$color || 'var(--indian-red)'};
    }
  }
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border-color: ${(props) => props.$color || 'var(--indian-red)'};
  }

  .ant-picker-footer .ant-picker-ok .ant-btn:not(:disabled) {
    background-color: ${(props) => props.$color || 'var(--indian-red)'};
    border-color: ${(props) => props.$color || 'var(--indian-red)'};
    span {
      color: white;
    }
    &:hover {
      background-color: white;
      border-color: ${(props) => props.$color || 'var(--indian-red)'};
      span {
        color: ${(props) => props.$color || 'var(--indian-red)'};
      }
    }
  }
`;

type DatePickerWrapperProps = Omit<React.ComponentProps<typeof AntDatePicker>, 'size'> & {
  size?: any;
};

const DatePickerWrapper = styled(AntDatePicker)<DatePickerWrapperProps>`
  padding: 0.719rem 1.438rem 0.719rem;

  &.ant-picker-large .ant-picker-input > input {
    font-size: 1rem;
    font-family: var(--font-family-regular);
    line-height: 1rem !important;
  }
  border-radius: 23.5px;

  ${({ size }) => {
    if (size === 'default') {
      return `height:2.5rem;`;
    }
  }}
`;

const TimePickerWrapper = styled(AntTimePicker)`
  .ant-picker-large .ant-picker-input > input {
    font-size: 0.875rem;
  }
  border-radius: 23.5px;
`;

const Legend = styled.span`
  padding: 0.25rem 0.75rem;
  &.today {
    border-radius: 6px;
    background-color: var(--orange);
    color: var(--white);
  }
  &.disabled {
    color: var(--grey);
    background-color: var(--grey-light);
  }
  &.overlapped {
    color: var(--green-dark);
    background-color: var(--grey);
  }
`;

type DatePickerProps = DatePickerWrapperProps & {
  overlappedFootnote?: string;
  disabledFootnote?: boolean;
  showTime?: boolean;
  fontFamily?: string;
  color?: string;
};

export default function DatePicker({
  disabledDate,
  overlappedFootnote,
  disabledFootnote,
  fontFamily,
  color,
  ...props
}: DatePickerProps) {
  return (
    <DatePickerWrapper
      disabledDate={disabledDate}
      suffixIcon={<CalendarIcon />}
      size="large"
      showToday
      panelRender={(node) => (
        <DatePickerPanelContainer $fontFamily={fontFamily} $color={color}>
          {node}
        </DatePickerPanelContainer>
      )}
      renderExtraFooter={() => {
        if (!overlappedFootnote && !disabledFootnote) {
          return null;
        }
        return (
          <div className="py-2">
            {overlappedFootnote && (
              <p className="m-0 text-xs">
                <Legend className="overlapped">1</Legend> {overlappedFootnote}
              </p>
            )}
            <p className="m-0 text-xs mt-2">
              <Legend className="disabled">5</Legend> Unavailable dates
            </p>
          </div>
        );
      }}
      {...props}
    />
  );
}

export function TimePicker(props: TimePickerProps) {
  return <TimePickerWrapper suffixIcon={<CalendarIcon />} {...props} size="large" />;
}
